import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { useOnStudentChange, useStudentCard } from '../../context/StudentCardContext';
import { getStudents, getAdminMentors, getStudentProgress } from '../../lib/api';
import type { StudentFull, MentorInfo, ClassRecord } from '../../lib/api';

// ==================== STATUS CONFIG ==========================

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  'Completed':     { bg: '#dcfce7', color: '#15803d' },
  'Task Given':    { bg: '#fef9c3', color: '#854d0e' },
  'Not Completed': { bg: '#fee2e2', color: '#b91c1c' },
};

// ==================== PROGRESS DETAIL (right pane) ===========

const ProgressDetail: React.FC<{
  student: StudentFull;
  mentorName: string;
  adminPassword: string;
}> = ({ student, mentorName, adminPassword }) => {
  const [records, setRecords] = useState<ClassRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setRecords([]);
    // pass empty mentorId â€” server ignores it for getStudentProgress
    getStudentProgress(student.studentId, '')
      .then(res => setRecords(res.records))
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [student.studentId, adminPassword]);

  const present   = records.filter(r => r.attendance === 'Present').length;
  const absent    = records.filter(r => r.attendance === 'Absent').length;
  const completed = records.filter(r => r.completionStatus === 'Completed').length;
  const attPct = records.length ? Math.round((present / records.length) * 100) : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-5 border-b border-gray-100 flex-shrink-0" style={{ background: '#f8f6ff' }}>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0"
            style={{ background: '#2b2240', color: '#F0B429' }}>
            {student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-mono font-bold px-2 py-0.5 rounded"
                style={{ background: '#2b2240', color: '#F0B429' }}>
                {student.studentId}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                student.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}>{student.status}</span>
            </div>
            <p className="font-bold text-gray-900 mt-1">{student.name}</p>
            <p className="text-xs text-gray-500">{student.course}</p>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div className="flex items-center gap-1.5 text-gray-600">
            <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span className="font-semibold text-gray-800">{mentorName || student.mentorId}</span>
            <span className="text-gray-400 text-xs">mentor</span>
          </div>
          {student.email && (
            <div className="flex items-center gap-1.5 text-gray-500 col-span-2 truncate">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              <span className="truncate">{student.email}</span>
            </div>
          )}
          {student.dateJoined && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8" fill="none">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <span>Joined {student.dateJoined}</span>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 border-b border-gray-100 divide-x divide-gray-100 flex-shrink-0">
        {[
          { label: 'Classes',  value: records.length,   color: '#2b2240' },
          { label: 'Present',  value: present,          color: '#16a34a' },
          { label: 'Absent',   value: absent,           color: '#dc2626' },
          { label: 'Att %',    value: `${attPct}%`,     color: attPct >= 80 ? '#16a34a' : attPct >= 60 ? '#d97706' : '#dc2626' },
        ].map(s => (
          <div key={s.label} className="py-3 text-center">
            <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Extra stats row */}
      <div className="border-b border-gray-100 flex-shrink-0">
        <div className="py-2 text-center">
          <div className="text-base font-bold text-green-700">{completed}</div>
          <div className="text-xs text-gray-400">Completed Tasks</div>
        </div>
      </div>

      {/* Class history */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading class history…</div>
      ) : records.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">No class records yet.</div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0" style={{ background: '#2b2240' }}>
              <tr>
                {['#', 'Date', 'Day', 'Taken By', 'Topic', 'Portion', 'Attendance', 'Status', 'Remark'].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-semibold text-white whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((r, idx) => {
                const sc = STATUS_STYLE[r.completionStatus] ?? { bg: '#f3f4f6', color: '#374151' };
                return (
                  <tr key={idx} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium whitespace-nowrap" style={{ color: '#2b2240' }}>{r.date}</td>
                    <td className="px-3 py-2 text-gray-500">{r.day}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.takenBy || 'â€”'}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[140px] truncate font-medium" title={r.topic}>{r.topic || 'â€”'}</td>
                    <td className="px-3 py-2 text-gray-500 max-w-[120px] truncate" title={r.portion}>{r.portion || 'â€”'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${r.attendance === 'Present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {r.attendance}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="px-1.5 py-0.5 rounded text-xs font-semibold" style={{ background: sc.bg, color: sc.color }}>
                        {r.completionStatus}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-400 max-w-[100px] truncate" title={r.remark}>{r.remark || 'â€”'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== STUDENT LIST ITEM ======================

const StudentListItem: React.FC<{
  student: StudentFull;
  mentorName: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ student, mentorName, isSelected, onClick }) => {
  const { onStudentEnter, onStudentMove, onStudentLeave } = useStudentCard();
  return (
  <div onClick={onClick} className={`px-4 py-3 border-b border-gray-100 cursor-pointer transition-colors ${
    isSelected ? 'bg-[#2b2240]' : 'hover:bg-gray-50'
  }`}>
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={isSelected
          ? { background: '#F0B429', color: '#2b2240' }
          : { background: '#f0edf7', color: '#2b2240' }}>
        {student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={`font-semibold text-sm truncate ${isSelected ? 'text-white' : 'text-gray-800'}`}
            onMouseEnter={e => { e.stopPropagation(); onStudentEnter(student.studentId, student.name, e); }}
            onMouseMove={onStudentMove}
            onMouseLeave={onStudentLeave}
          >
            {student.name}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className={`text-xs font-mono ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
            {student.studentId}
          </span>
          <span className={`text-xs ${isSelected ? 'text-white/50' : 'text-gray-400'}`}>·</span>
          <span className={`text-xs ${isSelected ? 'text-white/60' : 'text-indigo-600'}`}>{mentorName}</span>
        </div>
        <p className={`text-xs truncate mt-0.5 ${isSelected ? 'text-white/50' : 'text-gray-400'}`}>
          {student.course}
        </p>
      </div>
      {isSelected && (
        <svg className="w-4 h-4 text-white/50 flex-shrink-0" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  </div>
  );
};

// ==================== MAIN PAGE ==============================

const AdminStudentProgress: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const [students, setStudents] = useState<StudentFull[]>([]);
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StudentFull | null>(null);
  const [search, setSearch] = useState('');
  const [filterMentor, setFilterMentor] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([
        getStudents(adminPassword),
        getAdminMentors(adminPassword),
      ]);
      setStudents(sRes.students.filter(s => s.status === 'Active'));
      setMentors(mRes.mentors);
    } catch (err: any) {
      toast.error('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [adminPassword]);

  useEffect(() => { load(); }, [load]);
  // deactivated / reassigned from the student popup
  useOnStudentChange(() => { setSelected(null); load(); });

  const mentorMap = Object.fromEntries(mentors.map(m => [m.mentorId, m.mentorName]));

  const visible = students.filter(s => {
    if (filterMentor && s.mentorId !== filterMentor) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      s.studentId.toLowerCase().includes(q) ||
      s.course.toLowerCase().includes(q) ||
      (mentorMap[s.mentorId] || '').toLowerCase().includes(q)
    );
  });

  return (
    <TrackerLayout>
      <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 65px)' }}>
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold" style={{ color: '#2b2240' }}>Student Progress</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {students.length} active students &nbsp;·&nbsp; {mentors.length} mentors
            </p>
          </div>
          <button onClick={load}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading students…</div>
        ) : (
          <div className="flex-1 flex gap-4 min-h-0">
            {/* LEFT: Student list */}
            <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
              {/* Filters */}
              <div className="p-3 border-b border-gray-100 space-y-2">
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search students…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#2b2240]"
                  />
                </div>
                <select
                  value={filterMentor}
                  onChange={e => { setFilterMentor(e.target.value); setSelected(null); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:border-[#2b2240] bg-white"
                >
                  <option value="">All mentors</option>
                  {mentors.filter(m => m.status === 'Active').map(m => (
                    <option key={m.mentorId} value={m.mentorId}>{m.mentorName}</option>
                  ))}
                </select>
              </div>

              {/* Count */}
              <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-100">
                <span className="text-xs text-gray-500">{visible.length} student{visible.length !== 1 ? 's' : ''}</span>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {visible.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-xs">No students match your filters.</div>
                ) : (
                  visible.map(s => (
                    <StudentListItem
                      key={s.studentId}
                      student={s}
                      mentorName={mentorMap[s.mentorId] || s.mentorId}
                      isSelected={selected?.studentId === s.studentId}
                      onClick={() => setSelected(prev => prev?.studentId === s.studentId ? null : s)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* RIGHT: Progress detail */}
            <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
              {selected ? (
                <ProgressDetail
                  key={selected.studentId}
                  student={selected}
                  mentorName={mentorMap[selected.mentorId] || selected.mentorId}
                  adminPassword={adminPassword}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-gray-400">
                  <svg className="w-12 h-12 mb-3 text-gray-200" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                  <p className="font-semibold text-gray-500">Select a student</p>
                  <p className="text-sm mt-1">Choose a student from the list to view their class history and topic progress</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminStudentProgress;

